import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup, Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil, catchError } from 'rxjs/operators';
import { LazyScrollableDataSource } from '@rappi/common';
import { SelectComponent } from '@rappi/ui/select';
import { MakersFinancesMgrService } from '../../../../services/makers-finances-mgr.service';
import { NewPurchaseOrderConfig, InvestmentConfig } from '../../../../models/makers-finances-mgr.models';
import { StoreAuxService } from '../../../../../services/storeAux/store-aux.service';
import {
  DETAIL_MODE, DetailMode, SOURCE_TYPE, SourceType,
  PO_FIELD, PO_FIELD_LABELS, PO_SECTION_LABELS, PO_SECTION,
  INVESTMENT_FIELDS, InvestmentField,
  PO_TYPE, BUDGET_TYPE_LABEL, BUDGET_TYPE_OPTIONS, BudgetType,
} from '../../purchase-order.constants';
import { SelectOption, staticSource, staticSourceFromTaxOptions, staticSourceFromOptions } from '../section-base.types';

@Component({
  selector: 'app-section-budget-dates',
  templateUrl: './section-budget-dates.component.html',
  styleUrls: ['./section-budget-dates.component.scss'],
})
export class SectionBudgetDatesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() form: FormGroup;
  @Input() mode: DetailMode;
  @Input() sourceType: SourceType;
  @Input() remoteConfig: NewPurchaseOrderConfig | null;
  @Input() catalogsLoaded = false;
  @Input() submitted = false;
  @Input() fieldConfigs: Record<string, any> = {};
  // Edit mode: the PO id is passed as excludeId to the external-id availability check so the PO
  // doesn't conflict with itself.
  @Input() poId: number | null = null;
  // Flips true once the parent has patched the form from the loaded PO; triggers setSelection on
  // the currency/tax selects (one-select is not form-bound).
  @Input() poLoaded = false;
  // CMS rollout flag (FinancesConfig.budget_type_validation_enabled). While false, Budget type is
  // hidden (even for Partnerships) and never required on Submit (legacy behavior).
  @Input() budgetTypeValidationEnabled = false;
  // CMS rollout flag (FinancesConfig.trato_expiration_date_enabled). While false, Expiration date is
  // always hidden (even for an existing Trato PO with a persisted value) and never required
  // (legacy behavior) — see isExpirationDateVisible.
  @Input() tratoExpirationDateEnabled = false;

  // In edit mode the skeleton stays until the PO detail is also loaded (poLoaded), not just the
  // catalogs. In create mode only catalogsLoaded matters.
  get showSkeleton(): boolean {
    return !this.catalogsLoaded || (this.mode === DETAIL_MODE.EDIT && !this.poLoaded);
  }

  @ViewChild('poCurrencySelect') poCurrencySelect: SelectComponent<SelectOption>;
  @ViewChild('taxPercentageSelect') taxPercentageSelect: SelectComponent<SelectOption>;
  @ViewChild('budgetTypeSelect') budgetTypeSelect: SelectComponent<SelectOption>;

  private readonly destroy$ = new Subject<void>();

  // Non-blocking warning message from the external-id availability check. Shown visually below the
  // input but does NOT make the form invalid — the user can still save draft or submit. At submit
  // time, the parent component re-checks and may show a confirmation modal.
  externalIdWarning: string | null = null;

  constructor(
    private readonly storeAux: StoreAuxService,
    private readonly financesService: MakersFinancesMgrService,
  ) {}

  readonly F = PO_FIELD;
  readonly LABELS = PO_FIELD_LABELS;
  readonly sectionLabel = PO_SECTION_LABELS[PO_SECTION.BUDGET_AND_DATES];
  readonly selectConfig = { search: false, includeAll: false, showTotalsChip: false, maxNumberOfChips: 1, panelClass: '' };

  selectSources: Record<string, LazyScrollableDataSource<SelectOption>> = {};

  // Used both as the datepicker's [min] (can't set an expiration date in the past) and as the
  // default-fill value — see _maybeDefaultExpirationDate.
  readonly todayDate = new Date();

  private _investmentCfg: Record<InvestmentField, InvestmentConfig> = {
    [PO_FIELD.CO_INVESTMENT]: { max_percentage: 100 },
    [PO_FIELD.RE_INVESTMENT]: { max_percentage: 100 },
  };

  // Factory for creating investment max percentage validator
  private investmentMaxPercentageValidator(fieldKey: InvestmentField): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }
      const value = parseFloat(control.value);
      if (isNaN(value)) {
        return null;
      }
      const cfg = this._investmentCfg[fieldKey];
      if (value > cfg.max_percentage) {
        return { investmentExceeded: { max: cfg.max_percentage, actual: value } };
      }
      return null;
    };
  }

  ngOnInit(): void {
    // Static options, same per country/source — initial assignment for the first time the field
    // renders. Rebuilt again below on every re-entry into Partnerships (see the po_type subscription).
    this.selectSources[PO_FIELD.BUDGET_TYPE] = staticSourceFromOptions(BUDGET_TYPE_OPTIONS);

    // Budget type only applies to Partnerships POs, gated by budgetTypeValidationEnabled. po_type
    // lives in the same shared form (owned by section-general-info), so we react to it directly
    // rather than needing a new @Input. Leaving Partnerships silently clears the field — see
    // docs/plans/budget-type-conditional-field-frontend-plan.md.
    this.form.get(PO_FIELD.PO_TYPE)?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe((poType: string | null) => {
      if (poType !== PO_TYPE.PARTNERSHIPS) {
        this.form.get(PO_FIELD.BUDGET_TYPE)?.setValue(null, { emitEvent: false });
        this.budgetTypeSelect?.clearSelection();
      } else if (this.budgetTypeValidationEnabled) {
        // *ngIf destroys the one-select (and its cdk-virtual-scroll-viewport) when the field is
        // hidden, which calls disconnect() on the bound LazyScrollableDataSource and permanently
        // completes its internal streams. Reusing that same (now-disconnected) instance when the
        // field reappears renders a blank dropdown — rebuild it fresh every time we re-enter
        // Partnerships so the new one-select instance gets a live data source.
        this.selectSources[PO_FIELD.BUDGET_TYPE] = staticSourceFromOptions(BUDGET_TYPE_OPTIONS);
      }
      this._updateBudgetTypeValidator();
    });

    const poNumberCtrl = this.form.get(PO_FIELD.PO_NUMBER);

    poNumberCtrl?.valueChanges.pipe(
      debounceTime(600),
      distinctUntilChanged(),
      switchMap(value => {
        this.externalIdWarning = null;
        const companyId = this.form.get(PO_FIELD.COMPANY_TO_BILL)?.value;
        if (!value || !companyId) {
          return of(null);
        }
        return this.financesService.checkExternalIdAvailability(value, companyId, this.storeAux.country, this.poId ?? undefined).pipe(
          catchError(() => of(null))
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(res => {
      if (res?.data && !res.data.available) {
        this.externalIdWarning = res.data.warning
          || (res.data.conflicting_po_id != null
            ? `There is another PO (${res.data.conflicting_po_id}) using the same External ID`
            : 'External ID is already in use');
      } else {
        this.externalIdWarning = null;
      }
    });

    this.form.get(PO_FIELD.COMPANY_TO_BILL)?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.externalIdWarning = null;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['remoteConfig'] && this.remoteConfig) {
      this.selectSources[PO_FIELD.PO_CURRENCY] = staticSource(this.remoteConfig.currencies);
      if (this.remoteConfig.tax_percentage_options?.length) {
        this.selectSources[PO_FIELD.TAX_PERCENTAGE] = staticSourceFromTaxOptions(this.remoteConfig.tax_percentage_options);
      }
      INVESTMENT_FIELDS.forEach(key => {
        const investmentCfg = this.remoteConfig?.[key];
        if (investmentCfg) {
          this._investmentCfg[key] = investmentCfg;
        }
        // Apply investment max percentage validator to the control
        const ctrl = this.form?.get(key);
        if (ctrl) {
          ctrl.setValidators([this.investmentMaxPercentageValidator(key as InvestmentField)]);
          ctrl.updateValueAndValidity({ emitEvent: false });
        }
      });
      this._updateTaxPercentageValidator();
    }

    if (changes['sourceType']) {
      this._updateTaxPercentageValidator();
      this._maybeDefaultExpirationDate();
    }

    if (changes['poLoaded'] && this.poLoaded) {
      setTimeout(() => this._prefillSelects());
      // Edit mode patches po_type with emitEvent:false (see purchase-order-detail.component.ts's
      // _patchFormFromDetail), so the ngOnInit subscription above never fires for the initial load —
      // the validator must be reconciled explicitly here, same reasoning as _updateTaxPercentageValidator.
      this._updateBudgetTypeValidator();
    }

    // budgetTypeValidationEnabled arrives asynchronously from the CMS config fetch and can resolve
    // after po_type is already set to Partnerships (e.g. edit mode's silent patch, or the user
    // selecting it before the flag loads) — reconcile the validator once it lands.
    if (changes['budgetTypeValidationEnabled']) {
      this._updateBudgetTypeValidator();
    }

    // tratoExpirationDateEnabled arrives asynchronously too, and can resolve after source_type is
    // already Trato — the required-on-submit reconciliation happens at the parent (the validator is
    // set there), but the default-fill is local to this component and only otherwise triggered by a
    // source_type change, so reconcile it here as well.
    if (changes['tratoExpirationDateEnabled']) {
      this._maybeDefaultExpirationDate();
    }
  }

  // Edit mode: sync the currency/tax select UIs to the patched form values. The form already holds
  // the values; the selects just need their display set (one-select is not form-bound).
  private _prefillSelects(): void {
    const currency = this.form.get(PO_FIELD.PO_CURRENCY)?.value;
    if (currency && this.poCurrencySelect) {
      this.poCurrencySelect.setSelection({ id: currency, name: currency });
    }
    const taxValue = this.form.get(PO_FIELD.TAX_PERCENTAGE)?.value;
    if (taxValue != null && this.taxPercentageSelect && this.remoteConfig?.tax_percentage_options?.length) {
      const match = this.remoteConfig.tax_percentage_options.find(o => o.value === taxValue);
      if (match) {
        this.taxPercentageSelect.setSelection({ id: match.value, name: match.label });
      }
    }
    const budgetTypeValue = this.form.get(PO_FIELD.BUDGET_TYPE)?.value as BudgetType | null;
    if (budgetTypeValue && this.budgetTypeSelect) {
      this.budgetTypeSelect.setSelection({ id: budgetTypeValue, name: BUDGET_TYPE_LABEL[budgetTypeValue] });
    }
  }

  // Budget type only applies to Partnerships POs, and only while the CMS rollout flag is on — see
  // PO_TYPE in purchase-order.constants.ts and FinancesConfig.budget_type_validation_enabled.
  get isBudgetTypeVisible(): boolean {
    return this.budgetTypeValidationEnabled && this.form?.get(PO_FIELD.PO_TYPE)?.value === PO_TYPE.PARTNERSHIPS;
  }

  private _updateBudgetTypeValidator(): void {
    const ctrl = this.form?.get(PO_FIELD.BUDGET_TYPE);
    if (this.isBudgetTypeVisible) {
      ctrl?.setValidators([Validators.required]);
    } else {
      ctrl?.clearValidators();
    }
    ctrl?.updateValueAndValidity({ emitEvent: false });
  }

  get isInvestmentVisible(): boolean {
    return !!this.fieldConfigs[PO_FIELD.CO_INVESTMENT]?.visible_by_source?.[this.sourceType];
  }

  get isPoNumberVisible(): boolean {
    return !!this.fieldConfigs[PO_FIELD.PO_NUMBER]?.visible_by_source?.[this.sourceType];
  }

  get isTaxPercentageVisible(): boolean {
    return this.storeAux.country === 'BR'
      && !!this.fieldConfigs[PO_FIELD.TAX_PERCENTAGE]?.visible_by_source?.[this.sourceType]
      && !!(this.remoteConfig?.tax_percentage_options?.length);
  }

  getInvestmentExceeded(fieldKey: string): boolean {
    const cfg = this._investmentCfg[fieldKey as InvestmentField];
    if (!cfg) {
      return false;
    }
    const value = parseFloat(this.form?.get(fieldKey)?.value);
    return !isNaN(value) && value > cfg.max_percentage;
  }

  getInvestmentWarning(fieldKey: string): string | null {
    if (!this.getInvestmentExceeded(fieldKey)) {
      return null;
    }
    const cfg = this._investmentCfg[fieldKey as InvestmentField];
    const raw = cfg?.warning_message ?? 'The percentage exceeds the max recommended for this country';
    const maxPercentageLabel = `${cfg?.max_percentage}%`;
    return `*${raw.replace(/\{\{[^}]+\}\}/g, maxPercentageLabel)}`;
  }

  onSelectChange(fieldKey: string, value: SelectOption): void {
    this.form.get(fieldKey)?.setValue(value?.id ?? null);
  }

  isInvalid(key: string): boolean {
    const ctrl = this.form.get(key);
    if (!ctrl) {
      return false;
    }
    // For investment fields, show investmentExceeded error immediately (no submitted gate),
    // and show required error when the form has been submitted
    if (key === PO_FIELD.CO_INVESTMENT || key === PO_FIELD.RE_INVESTMENT) {
      if (!!ctrl.errors?.['investmentExceeded']) {
        return true;
      }
      return this.submitted && (!ctrl.value || !!ctrl.errors?.['required']);
    }
    if (!this.submitted) {
      return false;
    }
    return !ctrl?.value || !!ctrl.errors?.['required'];
  }

  private _updateTaxPercentageValidator(): void {
    const ctrl = this.form?.get(PO_FIELD.TAX_PERCENTAGE);
    if (this.isTaxPercentageVisible) {
      ctrl?.setValidators([Validators.required]);
    } else {
      ctrl?.clearValidators();
    }
    ctrl?.updateValueAndValidity({ emitEvent: false });
  }

  // Strictly tied to the CMS flag — no fallback for "already has a value". While off, Expiration
  // date never shows in create or edit, even for an existing Trato PO with a persisted value: the
  // control still holds that value (see _buildFormPatch) and is still resent on update, it's just
  // not rendered/editable in the UI. Editability (not visibility) while visible is gated by the
  // parent's isDraftEdit/_clearNonEditableForSource — see purchase-order-detail.component.ts.
  get isExpirationDateVisible(): boolean {
    return this.sourceType === SOURCE_TYPE.TRATO && this.tratoExpirationDateEnabled;
  }

  // Trato requires expiration_date up front; defaulted to today per the product requirement
  // ("Mantenemos la fecha inicial a enviar como hoy"). Only auto-fills in Create mode, only while
  // the flag is on, and only when still empty — never overwrites a value loaded from an existing PO.
  private _maybeDefaultExpirationDate(): void {
    if (this.mode !== DETAIL_MODE.CREATE || !this.tratoExpirationDateEnabled) {
      return;
    }
    if (this.sourceType !== SOURCE_TYPE.TRATO) {
      return;
    }
    const ctrl = this.form?.get(PO_FIELD.EXPIRATION_DATE);
    if (ctrl && !ctrl.value) {
      ctrl.setValue(this.todayDate, { emitEvent: false });
    }
  }
}
